'use client'

import { Grid, Divider, Typography, Box, Tooltip } from '@mui/material'

// Types
import type { BarChartHorizentalListItemProps } from '@/types/components/molecules/item/chart/bar-horizental'

// Utils
import ToPersianNumber from '@/utils/number/to-persian-number'

const BarChartHorizentalMolecule = (props: BarChartHorizentalListItemProps) => {
	// Props
	const { locale, title, maleNumber, femaleNumber, unknownNumber } = props

	// Variables
	const values = [maleNumber, femaleNumber, unknownNumber]
	const nonZeroCount = values.filter(v => v !== 0).length
	const gapValue = nonZeroCount === 1 ? 0 : 0.4
	const total = maleNumber + femaleNumber + unknownNumber
	const malePercent = Math.round((maleNumber / total) * 100)
	const femalePercent = Math.round((femaleNumber / total) * 100)
	const unknownPercent = Math.round((unknownNumber / total) * 100)

	return (
		<Grid
			container
			spacing={2}
			sx={{
				alignItems: 'stretch',
				height: 1
			}}
		>
			<Grid size={12}>
				<Grid
					container
					spacing={1}
					sx={{
						justifyContent: 'space-between',
						height: 1
					}}
				>
					<Grid size={12} sx={{ '& .MuiDivider-root': { bgcolor: '#e4e4e3' } }}>
						<Typography
							component="div"
							sx={{
								fontSize: { xs: 12, lg: 14 },
								fontWeight: 700
							}}
						>
							{title}
						</Typography>

						<Divider />
					</Grid>

					<Grid size={12}>
						<Box
							sx={{
								display: 'flex',
								gap: { gapValue },
								height: 13
							}}
						>
							<Tooltip describeChild title={`${ToPersianNumber(unknownPercent.toString())}%`}>
								<Box
									sx={{
										width: `${unknownPercent}%`,
										bgcolor: '#c2c2c1',
										borderTopRightRadius: nonZeroCount === 1 ? 8 : 0,
										borderBottomRightRadius: nonZeroCount === 1 ? 8 : 0,
										borderTopLeftRadius: 8,
										borderBottomLeftRadius: 8
									}}
								/>
							</Tooltip>

							<Tooltip describeChild title={`${ToPersianNumber(malePercent.toString())}%`}>
								<Box
									sx={{
										width: `${malePercent}%`,
										bgcolor: 'primary.main',
										borderTopRightRadius: nonZeroCount === 1 ? 8 : femaleNumber === 0 ? 8 : 0,
										borderBottomRightRadius: nonZeroCount === 1 ? 8 : femaleNumber === 0 ? 8 : 0,
										borderTopLeftRadius: nonZeroCount === 1 ? 8 : unknownNumber === 0 ? 8 : 0,
										borderBottomLeftRadius: nonZeroCount === 1 ? 8 : unknownNumber === 0 ? 8 : 0
									}}
								/>
							</Tooltip>

							<Tooltip describeChild title={`${ToPersianNumber(femalePercent.toString())}%`}>
								<Box
									sx={{
										width: `${femalePercent}%`,
										bgcolor: 'secondary.main',
										borderTopRightRadius: 8,
										borderBottomRightRadius: 8,
										borderTopLeftRadius: nonZeroCount === 1 ? 8 : 0,
										borderBottomLeftRadius: nonZeroCount === 1 ? 8 : 0
									}}
								/>
							</Tooltip>
						</Box>
					</Grid>

					<Grid size={12}>
						<Grid
							container
							sx={{
								justifyContent: 'space-between',
								alignItems: 'center'
							}}
						>
							<Grid
								size={6}
								sx={{
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<Box
									sx={{
										width: 10,
										height: 10,
										mr: 1,
										bgcolor: 'secondary.main'
									}}
								/>

								<Typography
									component="div"
									color="#414552"
									sx={{
										display: 'inline-block',
										fontSize: { xs: 12, lg: 16 },
										fontWeight: 400,
										lineHeight: 1,
										letterSpacing: -0.01
									}}
								>
									{locale.common.title.female}
								</Typography>
							</Grid>

							<Grid size="auto">
								<Typography
									component="div"
									color="#414552"
									sx={{
										fontSize: { xs: 12, lg: 16 },
										fontWeight: 400,
										lineHeight: 1,
										letterSpacing: -0.01
									}}
								>
									{ToPersianNumber(femaleNumber.toString())}
								</Typography>
							</Grid>
						</Grid>
					</Grid>

					<Grid size={12}>
						<Divider />
					</Grid>

					<Grid size={12}>
						<Grid
							container
							sx={{
								justifyContent: 'space-between',
								alignItems: 'center'
							}}
						>
							<Grid
								size={6}
								sx={{
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<Box
									sx={{
										width: 10,
										height: 10,
										mr: 1,
										bgcolor: 'primary.main'
									}}
								/>

								<Typography
									component="div"
									color="#414552"
									sx={{
										display: 'inline-block',
										fontSize: { xs: 12, lg: 16 },
										fontWeight: 400,
										lineHeight: 1,
										letterSpacing: -0.01
									}}
								>
									{locale.common.title.male}
								</Typography>
							</Grid>

							<Grid size="auto">
								<Typography
									component="div"
									color="#414552"
									sx={{
										fontSize: { xs: 12, lg: 16 },
										fontWeight: 400,
										lineHeight: 1,
										letterSpacing: -0.01
									}}
								>
									{ToPersianNumber(maleNumber.toString())}
								</Typography>
							</Grid>
						</Grid>
					</Grid>

					<Grid size={12}>
						<Divider />
					</Grid>

					<Grid size={12}>
						<Grid
							container
							sx={{
								justifyContent: 'space-between',
								alignItems: 'center'
							}}
						>
							<Grid
								size={6}
								sx={{
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<Box
									sx={{
										width: 10,
										height: 10,
										mr: 1,
										bgcolor: '#c2c2c1'
									}}
								/>

								<Typography
									component="div"
									color="#414552"
									sx={{
										display: 'inline-block',
										fontSize: { xs: 12, lg: 16 },
										fontWeight: 400,
										lineHeight: 1,
										letterSpacing: -0.01
									}}
								>
									{locale.common.title.unknown}
								</Typography>
							</Grid>

							<Grid size="auto">
								<Typography
									component="div"
									color="#414552"
									sx={{
										fontSize: { xs: 12, lg: 16 },
										fontWeight: 400,
										lineHeight: 1,
										letterSpacing: -0.01
									}}
								>
									{ToPersianNumber(unknownNumber.toString())}
								</Typography>
							</Grid>
						</Grid>
					</Grid>

					<Grid size={12}>
						<Divider />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default BarChartHorizentalMolecule
